import { FC, ReactNode, DOMAttributes } from 'react'
import UiIcon, { UiIconName } from '../Icon'

type ButtonProps = {
  children: ReactNode
  variant?: 'primary' | 'ghost' | 'link' | 'dashed'
  icon?: UiIconName
  iconPosition?: 'start' | 'end'
  isDisabled?: boolean
  isLoading?: boolean
  isBlock?: boolean
  type?: 'button' | 'submit' | 'reset'
  onClick?: DOMAttributes<HTMLButtonElement>['onClick']
  className?: string
}

const VARIANT_CLASSES: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-ink border border-ink text-white hover:bg-night',
  ghost: 'bg-transparent border border-ui-gray-200 text-ui-gray-700 hover:border-ink hover:text-ink',
  link: 'bg-transparent border-0 text-ui-gray-700 underline underline-offset-[3px] hover:text-ink h-auto px-0 py-2 rounded-none',
  dashed: 'bg-white border border-dashed border-ui-gray-500 text-ui-gray-700 hover:border-ink hover:text-ink',
}

const Button: FC<ButtonProps> = ({
  children,
  variant = 'primary',
  icon,
  iconPosition = 'start',
  isDisabled = false,
  isLoading = false,
  isBlock = false,
  type = 'button',
  onClick,
  className = '',
}) => {
  const isInactive = isDisabled || isLoading
  const base = variant === 'link'
    ? 'inline-flex items-center justify-center gap-2 font-aeonik text-[15px] font-medium transition-colors duration-150'
    : 'inline-flex h-[54px] items-center justify-center gap-2.5 rounded-xl px-6 font-aeonik text-[15px] font-medium transition-colors duration-150 active:translate-y-px'
  const disabledClasses = isInactive && variant !== 'link'
    ? 'cursor-not-allowed border-ui-gray-200 bg-ui-gray-200 text-ui-gray-600'
    : ''

  return (
    <button
      // eslint-disable-next-line react/button-has-type
      type={type}
      disabled={isInactive}
      onClick={isInactive ? undefined : onClick}
      className={`${base} ${disabledClasses || VARIANT_CLASSES[variant]} ${isBlock ? 'w-full' : ''} ${className}`}
    >
      {isLoading && (
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-current/30 border-t-current"
          aria-hidden="true"
        />
      )}
      {!isLoading && icon && iconPosition === 'start' && <UiIcon name={icon} size={14} />}
      {children}
      {!isLoading && icon && iconPosition === 'end' && <UiIcon name={icon} size={14} />}
    </button>
  )
}

export default Button
